<template>
    <FiltersModal
        :is-open
        :loading
        :route-reset="filterRoutes.routeReset"
        :result-count
        :active-filter-count="chips.length"
        :fetching="loading"
        @close="() => $emit('close')"
    >
        <template #indicator>
            <ProgressIndicatorLinear :loading />
        </template>

        <template #chips>
            <FilterChips
                v-qa="'filter-chips-filter'"
                :chips
                :route-reset="filterRoutes.routeReset"
            />
        </template>

        <template #filters>
            <FilterAccordion
                :count="selected.price?.max && selected.price?.min ? 1 : 0"
                hide-count
                :title="t('catalogue.filters.price')"
            >
                <FilterPriceRange
                    :disabled="loading"
                    :filter="available.price"
                    :formatter
                    :value="selected.price"
                    @update="priceUpdated"
                />
            </FilterAccordion>

            <FilterAccordion
                v-qa="'category-filter'"
                :count="selectedProductTypes?.length"
                :title="t('catalogue.filters.jewellery_categories')"
            >
                <FilterLabel
                    :disabled="loading"
                    :filter="productTypes"
                    :get-route-add="filterRoutes.getRouteAddJewelleryType"
                    :get-route-remove="filterRoutes.getRouteRemoveJewelleryType"
                    :selected="selectedProductTypes ?? []"
                />
            </FilterAccordion>

            <FilterAccordion
                v-qa="'metal-filter'"
                :count="selected.primaryMetalCodes?.length"
                :title="t('catalogue.filters.metal')"
            >
                <FilterMetal
                    :disabled="loading"
                    :filter="available.primaryMetals"
                    :get-route-add-metal="filterRoutes.getRouteAddMetal"
                    :get-route-remove-metal="filterRoutes.getRouteRemoveMetal"
                    :selected="selected.primaryMetalCodes ?? []"
                />
            </FilterAccordion>

            <FilterAccordion
                v-if="
                    available.__typename !== 'CatalogueJewelleriesFilters' &&
                    'productStyles' in selected
                "
                v-qa="'product-style-filter'"
                :count="selected.productStyles?.length"
                :title="t('catalogue.filters.product_style')"
            >
                <!-- Component generic not working -->
                <!-- Function example below testGeneric() works -->
                <!-- Expect error until vue is fixed -->
                <!-- @vue-expect-error -->
                <FilterLabel
                    :disabled="loading"
                    :filter="
                        available.productStyles as FilterProductStylesAvailable[]
                    "
                    :get-route-add="filterRoutes.getRouteAddProductStyle"
                    :get-route-remove="filterRoutes.getRouteRemoveProductStyle"
                    :selected="selected.productStyles ?? []"
                />
            </FilterAccordion>

            <FilterAccordion
                v-qa="'gemstone-filter'"
                :count="selected.gemstones?.length"
                :title="t('catalogue.filters.gemstone')"
            >
                <FilterLabel
                    :disabled="loading"
                    :filter="available.gemstones"
                    :get-route-add="filterRoutes.getRouteAddGemstone"
                    :get-route-remove="filterRoutes.getRouteRemoveGemstone"
                    :selected="selected.gemstones ?? []"
                />
            </FilterAccordion>

            <FilterAccordion
                v-qa="'design-style-filter'"
                :count="selected.designStyles?.length"
                :title="t('catalogue.filters.design_style')"
            >
                <FilterLabel
                    :disabled="loading"
                    :filter="available.designStyles"
                    :get-route-add="filterRoutes.getRouteAddDesignStyle"
                    :get-route-remove="filterRoutes.getRouteRemoveDesignStyle"
                    :selected="selected.designStyles ?? []"
                />
            </FilterAccordion>
        </template>
    </FiltersModal>
</template>

<script lang="ts" setup>
import { computed, toRef } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import type {
    JewelleryBraceletProductStyle,
    JewelleryDesignFeature,
    JewelleryDesignStyle,
    JewelleryEarringsProductStyle,
    JewelleryGemstoneCode,
    JewelleryNecklaceProductStyle,
    MetalCode,
} from '@/graphql/api'

import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import type { JewelleryProductType } from '@/router'
import type { JewelleryProductTypes } from '@/views/product/jewellery/useJewelleryData'
import type {
    FilterProductStylesAvailable,
    Filters,
} from '@/views/product/jewellery/Jewelleries.vue'
import { clearMultiMetal } from './clearMultiMetal'

import FiltersModal from '@/components/ui/filter/FiltersModal.vue'
import FilterPriceRange from '@/components/ui/filter/filters/FilterPriceRange.vue'
import FilterMetal from '@/components/ui/filter/filters/FilterMetal.vue'
import FilterLabel from '@/components/ui/filter/filters/FilterLabel.vue'
import ProgressIndicatorLinear from '@/components/ui/indicators/ProgressIndicatorLinear.vue'
import FilterChips, {
    type FilterChip,
} from '@/components/ui/filter/FilterChips.vue'
import FilterAccordion from '@/components/ui/filter/FilterAccordion.vue'
import type { InputRangeModel } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'
import { useMoneyFormatter } from '@/plugins/money-formatter.ts'

const props = defineProps<{
    filters: Filters
    productTypes: JewelleryProductTypes
    selectedProductTypes?: JewelleryProductType[]
    resultCount: number
    isOpen: boolean
    filterRoutes: {
        getRouteAddMetal: (metal: MetalCode) => ButtonRouteRelative
        getRouteRemoveMetal: () => ButtonRouteRelative
        getRouteAddProductStyle: (
            code:
                | JewelleryBraceletProductStyle
                | JewelleryNecklaceProductStyle
                | JewelleryEarringsProductStyle
        ) => ButtonRouteRelative
        getRouteRemoveProductStyle: () => ButtonRouteRelative
        getRouteAddJewelleryType: (
            type: JewelleryProductType
        ) => ButtonRouteRelative
        getRouteRemoveJewelleryType: () => ButtonRouteRelative
        getRouteAddGemstone: (
            code: JewelleryGemstoneCode
        ) => ButtonRouteRelative
        getRouteRemoveGemstone: () => ButtonRouteRelative
        getRouteAddDesignStyle: (
            code: JewelleryDesignStyle
        ) => ButtonRouteRelative
        getRouteRemoveDesignStyle: () => ButtonRouteRelative
        getRouteAddDesignFeature: (
            code: JewelleryDesignFeature
        ) => ButtonRouteRelative
        getRouteRemoveDesignFeature: (
            code: JewelleryDesignFeature
        ) => ButtonRouteRelative
        getRoutePrice: (values: InputRangeModel) => ButtonRouteRelative
        routeReset: ButtonRouteRelative
    }
    loading: boolean
    chips: FilterChip[]
}>()

defineEmits<{
    close: []
}>()

const { t } = useI18n()

const router = useRouter()
const priceUpdated = (values: InputRangeModel) => {
    router.push(props.filterRoutes.getRoutePrice(values))
}

const available = toRef(() => props.filters.available)
const selected = computed(() => clearMultiMetal(props.filters.selected))

const formatter = useMoneyFormatter()
// function testGeneric<
//     I extends string,
//     O extends {
//         label: {
//             systemCode: I
//             translation: { name: string }
//             icon: { url: string } | null
//         }
//         totalResults: number
//     },
// >(selected: I[], available: O[]): void {}

// if (
//     available.value.__typename !== 'CatalogueJewelleriesFilters' &&
//     'productStyles' in selected.value
// ) {
//     testGeneric(
//         selected.value.productStyles || [],
//         available.value.productStyles as FilterProductStylesAvailable[]
//     )
// }
</script>
